import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useMe } from '../lib/queries';
import { Badge, ErrorMessage, Loading, PageHeader } from '../components/ui/Feedback';

export interface Quota {
  key: string;
  used: number;
  /** null = unlimited */
  limit: number | null;
}

export function useUsage() {
  return useQuery<{ plan_slug: string; quotas: Quota[] }>({
    queryKey: ['usage'],
    queryFn: () => api('/api/usage'),
  });
}

const QUOTA_LABELS: Record<string, { label: string; note?: string }> = {
  podcasts: { label: 'Shows' },
  episodes_per_month: { label: 'New episodes this month', note: 'Resets on the 1st.' },
  ai_transcription_minutes: { label: 'Transcription this month', note: 'Minutes of audio. Resets on the 1st.' },
};

/** What each plan feature is, in plain words; "soon" = in the plan, not built yet. */
const FEATURES: Array<{ key: string; label: string; soon?: boolean }> = [
  { key: 'ai_show_notes', label: 'AI show notes, chapters, titles, highlights and social posts' },
  { key: 'ai_artwork', label: 'AI cover art' },
  { key: 'sponsorship_engine', label: 'Sponsors: AI prospecting, pitches and pipeline' },
  { key: 'video_export', label: 'Video export', soon: true },
];

export function usageTone(q: Quota): 'success' | 'warning' | 'danger' {
  if (q.limit === null || q.limit === 0) return q.limit === 0 && q.used > 0 ? 'danger' : 'success';
  const share = q.used / q.limit;
  return share >= 1 ? 'danger' : share >= 0.8 ? 'warning' : 'success';
}

const BAR: Record<ReturnType<typeof usageTone>, string> = {
  success: 'bg-accent',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

export function SettingsPage() {
  const me = useMe();
  const usage = useUsage();
  if (me.isLoading || usage.isLoading) return <Loading />;
  if (me.isError) return <ErrorMessage error={me.error} onRetry={() => me.refetch()} />;
  if (usage.isError) return <ErrorMessage error={usage.error} onRetry={() => usage.refetch()} />;
  const plan = me.data!.plan_slug;
  const entitlements = me.data!.entitlements;

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" description="Your plan, what it includes, and how much of it you’ve used." />

      <section aria-labelledby="plan-heading" className="space-y-4 rounded-xl border border-border p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="plan-heading" className="text-lg font-semibold">
            {plan.charAt(0).toUpperCase() + plan.slice(1)} plan
          </h2>
          <span className="text-sm text-muted">Change plans on your site’s account page.</span>
        </div>
        <ul className="grid gap-4 sm:grid-cols-3">
          {usage.data!.quotas.map((q) => (
            <QuotaMeter key={q.key} quota={q} />
          ))}
        </ul>
      </section>

      <section aria-labelledby="features-heading" className="space-y-3 rounded-xl border border-border p-5">
        <h2 id="features-heading" className="text-lg font-semibold">
          Included
        </h2>
        <ul className="space-y-2 text-sm">
          {FEATURES.map((f) => {
            const on = entitlements[f.key] === true;
            return (
              <li key={f.key} className="flex items-center gap-2">
                <span aria-hidden className={on ? 'text-success' : 'text-muted'}>
                  {on ? '✓' : '–'}
                </span>
                <span className={on ? 'text-text' : 'text-muted'}>{f.label}</span>
                {!on && <Badge tone="neutral">Higher plan</Badge>}
                {on && f.soon && <Badge tone="neutral">Coming soon</Badge>}
                <span className="sr-only">{on ? '(included)' : '(not included)'}</span>
              </li>
            );
          })}
        </ul>
      </section>

      <section aria-labelledby="account-heading" className="space-y-2 rounded-xl border border-border p-5 text-sm">
        <h2 id="account-heading" className="text-lg font-semibold">
          Account
        </h2>
        <p>
          Signed in as <strong>{me.data!.user.email ?? 'your site account'}</strong>
          {me.data!.user.role ? ` (${me.data!.user.role})` : ''}.
        </p>
        <p className="text-muted">Your sign-in, billing and team are managed by your site.</p>
      </section>
    </div>
  );
}

function QuotaMeter({ quota: q }: { quota: Quota }) {
  const meta = QUOTA_LABELS[q.key] ?? { label: q.key.replace(/_/g, ' ') };
  const tone = usageTone(q);
  const pct = q.limit ? Math.min(100, Math.round((q.used / q.limit) * 100)) : 0;
  return (
    <li className="space-y-1.5 rounded-lg bg-surface p-4">
      <div className="text-xs text-muted">{meta.label}</div>
      <div className="text-xl font-semibold text-text">
        {q.used.toLocaleString()}
        <span className="text-sm font-normal text-muted"> {q.limit === null ? '· unlimited' : `of ${q.limit.toLocaleString()}`}</span>
      </div>
      {q.limit !== null && (
        <div
          role="progressbar"
          aria-label={`${meta.label} used`}
          aria-valuemin={0}
          aria-valuemax={q.limit}
          aria-valuenow={q.used}
          className="h-1.5 overflow-hidden rounded-full bg-surface-2"
        >
          <div className={`h-full ${BAR[tone]}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {tone === 'danger' && <p className="text-xs text-danger">Limit reached. Upgrade for more.</p>}
      {tone === 'warning' && <p className="text-xs text-warning">Close to your limit.</p>}
      {meta.note && <p className="text-xs text-muted">{meta.note}</p>}
    </li>
  );
}
